<template>
  <layout-basic>
    <section class="result-section">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" sm="10" md="7" lg="6">
            <v-card class="result-card" flat>
              <!-- Polling -->
              <div v-if="state === 'polling'" class="result-body">
                <v-progress-circular
                  indeterminate
                  size="52"
                  width="4"
                  color="#08a0a8"
                  class="result-spinner"
                ></v-progress-circular>
                <h1 class="font-sora result-title">Confirming your payment</h1>
                <p class="result-text">
                  This usually takes a few seconds. Please keep this page open.
                </p>
              </div>

              <!-- Pay by invoice: issued, awaiting payment. The page keeps a
                   slow poll going so a payment that arrives while it is open
                   flips it to the confirmed state without a reload. The
                   invoice takes bank transfer only (6 Oct 2026). -->
              <div v-else-if="state === 'invoice'" class="result-body">
                <span class="result-badge" aria-hidden="true">✔</span>
                <h1 class="font-sora result-title">Your invoice is on its way</h1>
                <p class="result-text">
                  Your agreement is signed and your invoice has been emailed to
                  you<template v-if="invoice.due_at">, payable by {{ dueDate }}</template>.
                  Please pay by bank transfer, using the bank details on the invoice.
                </p>
                <p class="result-note">
                  <a v-if="invoice.url" :href="invoice.url" rel="noopener" target="_blank" class="download-link">
                    View and pay your invoice
                  </a>
                </p>
                <p class="result-note">
                  Your IMPROVE account is created as soon as the payment is received.
                  We will email your sign in details and a copy of your signed
                  agreement then. Questions: <a href="mailto:info@improve.online">info@improve.online</a>.
                </p>
              </div>

              <!-- Provisioned -->
              <div v-else-if="state === 'success'" class="result-body">
                <span class="result-badge" aria-hidden="true">✔</span>
                <h1 class="font-sora result-title">Payment confirmed</h1>
                <!-- A trial owner who bought keeps the login they have: no set
                     password email is sent, so do not tell them to wait for one. -->
                <template v-if="upgraded">
                  <p class="result-text">
                    Your existing IMPROVE account is now live on your chosen plan,
                    with everything you set up during your trial.
                  </p>
                  <p class="result-note">
                    <a :href="loginHref" class="download-link">Sign in to your account</a>
                  </p>
                </template>
                <template v-else>
                  <p class="result-text">
                    Your IMPROVE account is being set up. Check your inbox for an
                    email to set your password and sign in.
                  </p>
                  <p class="result-note">
                    If nothing arrives in the next few minutes, please check your
                    spam or junk folder.
                  </p>
                </template>
                <p class="result-note">
                  <a :href="contractPdfUrl" rel="noopener" class="download-link">
                    Download your signed agreement (PDF)
                  </a>
                </p>
              </div>

              <!-- Still confirming after all attempts -->
              <div v-else-if="state === 'delayed'" class="result-body">
                <span class="result-badge" aria-hidden="true">✔</span>
                <h1 class="font-sora result-title">Payment is taking a little longer to confirm</h1>
                <p class="result-text">
                  You will receive your welcome email shortly. If nothing arrives
                  within an hour, contact
                  <a href="mailto:info@improve.online">info@improve.online</a>.
                </p>
                <p class="result-note">
                  Your copy of the signed agreement will be ready to download
                  here once your payment is confirmed.
                </p>
              </div>

              <!-- No uuid or request error -->
              <div v-else class="result-body">
                <h1 class="font-sora result-title">We could not find your purchase</h1>
                <p class="result-text">
                  The link may have expired. You can start again below, it only
                  takes a couple of minutes.
                </p>
                <router-link to="/get-started" class="result-link">
                  Back to Buy IMPROVE
                </router-link>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PurchaseAPI from '@/Api/purchase.js';
import { getLoginHref } from '@/config/ctas';

const STORAGE_KEY = 'improve_purchase_uuid';
const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 40;

export default {
  name: 'GetStartedSuccess',
  components: { LayoutBasic },
  data() {
    return {
      uuid: null,
      state: 'polling', // polling | invoice | success | delayed | notfound
      // Pay by invoice details from the status endpoint (url, due_at).
      invoice: {},
      pollInterval: POLL_INTERVAL_MS,
      // The purchase upgraded an existing trial account (no set password
      // email is sent in that case), from the status endpoint's flag.
      upgraded: false,
      attempts: 0,
      timer: null,
    };
  },
  metaInfo() {
    return {
      title: 'Confirming your payment',
      meta: [{ name: 'robots', content: 'noindex' }],
    };
  },
  created() {
    this.uuid =
      this.$route.query.purchase || PurchaseAPI.storageGet(STORAGE_KEY) || null;
    if (!this.uuid) {
      this.state = 'notfound';
      return;
    }
    this.checkStatus();
  },
  beforeDestroy() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
  },
  computed: {
    contractPdfUrl() {
      return this.uuid ? PurchaseAPI.contractPdfUrl(this.uuid) : '#';
    },
    loginHref() {
      return getLoginHref();
    },
    dueDate() {
      if (!this.invoice || !this.invoice.due_at) return '';
      const d = new Date(String(this.invoice.due_at).replace(' ', 'T') + 'Z');
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    },
  },
  methods: {
    checkStatus() {
      if (this.stopped) return;
      this.attempts += 1;
      PurchaseAPI.status(this.uuid)
        .then((res) => {
          const status = res && res.data ? res.data.status : null;
          if (status === 'provisioned') {
            this.upgraded = !!(res.data && res.data.upgrades_existing_account);
            this.state = 'success';
            PurchaseAPI.storageRemove(STORAGE_KEY);
          } else if (status === 'expired') {
            this.state = 'notfound';
          } else if (status === 'invoice_sent') {
            // Awaiting the invoice payment: show the panel and keep a slow
            // poll (30 days is the term, not 2 minutes), so a card payment
            // made straight away from the link still lands here.
            this.invoice = (res.data && res.data.invoice) || {};
            this.state = 'invoice';
            this.pollInterval = 15000;
            this.attempts = 0;
            this.scheduleNext();
          } else {
            this.scheduleNext();
          }
        })
        .catch((err) => {
          // Only a definitive "this purchase does not exist" ends the poll.
          // The customer HAS just paid: a network blip or a 5xx must keep
          // polling until the attempts cap degrades to the friendly
          // "taking a little longer" state, never claim the purchase is gone.
          const httpStatus = err && err.response ? err.response.status : null;
          if (httpStatus === 404 || httpStatus === 410) {
            this.state = 'notfound';
            return;
          }
          this.scheduleNext();
        });
    },
    scheduleNext() {
      if (this.stopped) return;
      if (this.attempts >= MAX_ATTEMPTS) {
        // An invoice awaiting payment is not "delayed": leave the panel up
        // and simply stop polling.
        if (this.state !== 'invoice') this.state = 'delayed';
        return;
      }
      this.timer = setTimeout(this.checkStatus, this.pollInterval);
    },
  },
};
</script>

<style scoped>
.result-section {
  --navy: var(--brand-navy, #002458);
  --teal: var(--brand-teal, #08a0a8);
  --grey: var(--brand-grey, #6b7280);
  --line: #e6ebf1;
  --tint: #eef8f8;
  background: #f7f9fb;
  padding: 72px 0 110px;
  min-height: 60vh;
}

.result-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 44px 36px 46px;
  box-shadow: 0 12px 40px rgba(0, 36, 88, 0.06);
}

.result-body {
  text-align: center;
}

.result-spinner {
  margin-bottom: 22px;
}

.result-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--teal);
  font-size: 22px;
  margin-bottom: 18px;
}

.result-title {
  font-size: 26px;
  line-height: 1.25;
  font-weight: 700;
  color: var(--navy);
  margin-bottom: 12px;
}

.result-text {
  font-size: 16px;
  color: #2c3a4b;
  margin: 0 0 10px;
}

.result-text a {
  color: var(--teal);
  font-weight: 600;
  text-decoration: none;
}

.result-note {
  font-size: 13.5px;
  color: var(--grey);
  margin: 0;
}

.result-link {
  display: inline-block;
  margin-top: 10px;
  color: var(--teal);
  font-weight: 700;
  text-decoration: none;
}

@media screen and (max-width: 600px) {
  .result-section {
    padding: 44px 0 80px;
  }

  .result-card {
    padding: 32px 20px 34px;
  }

  .result-title {
    font-size: 22px;
  }
}
.download-link {
  color: #08a0a8;
  font-weight: 600;
  text-decoration: underline;
}
</style>
